import React, { ReactNode } from 'react';
import classNames from 'classnames';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  Icons,
  Button,
  ToolButton,
} from '../';
import { IconPresentationProvider } from '@ohif/ui-next';

import NavBar from '../NavBar';

// Todo: we should move this component to composition and remove props base

interface HeaderProps {
  children?: ReactNode;
  menuOptions: Array<{
    title: string;
    icon?: string;
    onClick: () => void;
  }>;
  isReturnEnabled?: boolean;
  onClickReturnButton?: () => void;
  isSticky?: boolean;
  WhiteLabeling?: {
    createLogoComponentFn?: (React: any, props: any) => ReactNode;
  };
  PatientInfo?: ReactNode;
  Secondary?: ReactNode;
  UndoRedo?: ReactNode;
  ExitButton?: ReactNode;
  /** Hide the gear/settings dropdown (e.g. in the focused Segmentation workspace,
   *  where the single Exit button takes its place). */
  hideMenu?: boolean;
}

function Header({
  children,
  menuOptions,
  isReturnEnabled = true,
  onClickReturnButton,
  isSticky = false,
  WhiteLabeling,
  PatientInfo,
  UndoRedo,
  Secondary,
  ExitButton,
  hideMenu = false,
  ...props
}: HeaderProps): ReactNode {
  const onClickReturn = () => {
    if (isReturnEnabled && onClickReturnButton) {
      onClickReturnButton();
    }
  };

  return (
    <IconPresentationProvider
      size="large"
      IconContainer={ToolButton}
    >
      <NavBar
        isSticky={isSticky}
        {...props}
      >
        <div className="flex h-[52px] items-center gap-2 px-1">
          {/* LEFT ZONE: return-to-list + logo (+ secondary tools on large screens) */}
          <div className="flex shrink-0 items-center">
            <div
              className={classNames(
                'inline-flex items-center',
                isReturnEnabled && 'cursor-pointer'
              )}
              onClick={onClickReturn}
              data-cy="return-to-work-list"
            >
              {isReturnEnabled && (
                <Icons.ArrowLeft className="text-primary ml-1 h-7 w-7 shrink-0" />
              )}
              {/* Logo/wordmark intentionally removed — the back arrow handles
                  navigation and the toolbar gets the full width. */}
            </div>
            <div className="ml-2 hidden h-8 items-center lg:flex">{Secondary}</div>
          </div>

          {/* CENTER ZONE: primary toolbar in a floating, rounded "app-bar" pill —
              grows, and scrolls horizontally when space is tight */}
          <div className="flex min-w-0 flex-1 justify-center">
            <div className="bg-muted/40 ring-border/60 flex items-center space-x-0.5 overflow-x-auto rounded-2xl px-1.5 py-0.5 ring-1 ring-inset lg:space-x-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {children}
            </div>
          </div>

          {/* RIGHT ZONE: Exit (MPR/3D, self-hides in 2D) + settings. Patient
              details live in the viewport overlay; undo/redo on the measure bar. */}
          <div className="flex shrink-0 select-none items-center gap-1.5">
            {ExitButton}
            <div className={classNames('flex-shrink-0', hideMenu && 'hidden')}>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-primary hover:bg-muted h-9 w-9"
                  >
                    <Icons.GearSettings />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  {menuOptions.map((option, index) => {
                    const IconComponent = option.icon
                      ? Icons[option.icon as keyof typeof Icons]
                      : null;
                    return (
                      <DropdownMenuItem
                        key={index}
                        onSelect={option.onClick}
                        className="flex items-center gap-2 py-2"
                      >
                        {IconComponent && (
                          <span className="flex h-4 w-4 items-center justify-center">
                            <Icons.ByName name={option.icon} />
                          </span>
                        )}
                        <span className="flex-1">{option.title}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </div>
      </NavBar>
    </IconPresentationProvider>
  );
}

export default Header;
